import React, { useState, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { InView } from 'react-intersection-observer';
import { connect } from 'react-redux';
import some from 'lodash/some';
import get from 'lodash/get';

import { addToCart, addAll } from 'Redux/ducks/cart';
import { countryName } from 'Helpers/Geo/data';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import store, { history } from 'Redux/store';
import useViewport from 'Hooks/useViewport';
import AddToCart from 'Components/Button/Cart/AddToCart';

const DockedAddToCart = lazy(() => import(/* webpackChunkName: "DockedAddToCart" */ 'Components/InfoBar/DockedAddToCart/DockedAddToCart'));
const StickyAddToCart = lazy(() => import(/* webpackChunkName: "StickyAddToCart" */ 'Components/InfoBar/StickyAddToCart/StickyAddToCart'));

// Revist and refactor this component - also remove boiler plate from ProductCommerceDetail and BundleCommerceDetails component
const ProductAddToCartBtn = ({
  product,
  allSlugs,
  subscribed,
  localNewsletterConsent,
  voucher,
  showStickyHeader,
  oldStyle,
  preSelectedPnm,
  selectedTier,
  inCartTier,
}) => {
  const [addingToBasket, setAddingToBasket] = useState(false);

  const { isAboveMd, isBelowLg } = useViewport();

  const discountPercentOverride = get(product, 'discount_percent_override', 0);
  const excludedRegions = get(product, 'regions_excluded', []);
  const giveaway = get(product, 'giveaway', false);
  const giveawayNewsletter = get(product, 'giveaway_newsletter', false);
  const hideDiscount = get(product, 'hideDiscount', false);
  const includedRegions = get(product, 'regions_included', []);
  const payWhatYouWant = get(product, 'pay_what_you_want', false);
  const productId = get(product, '_id', '');
  const slug = get(product, 'slug', '');
  const soldOut = get(product, 'sold_out', false);

  let inCart = allSlugs.includes(slug);

  if (preSelectedPnm) {
    const productSlugs = get(product, 'bundles[0].games', []).map(game => game.slug);
    inCart = productSlugs.every(productSlug => allSlugs.includes(productSlug));
  }

  const dates = ProductRequirements.ReleaseDates(product);
  const validCoupon = ProductRequirements.validCoupon(product, voucher);
  let productHasValidCoupon = !!validCoupon;
  const totalSaving = ProductRequirements.totalSaving(product, validCoupon);
  if (totalSaving.isMonetary) productHasValidCoupon = false;

  let disableButton = addingToBasket;
  if (
    giveaway &&
    giveawayNewsletter &&
    !subscribed &&
    !localNewsletterConsent
  ) {
    disableButton = true;
  }

  let LockedInRegion = false;
  if (excludedRegions.length >= 1 && some(excludedRegions, { name: countryName })) {
    LockedInRegion = true;
  }
  if (includedRegions.length >= 1 && !some(includedRegions, { name: countryName })) {
    LockedInRegion = true;
  }

  if (soldOut && !LockedInRegion && !payWhatYouWant) return false;

  const clickAddToCart = async (tierIndex) => {
    setAddingToBasket(true);
    if (preSelectedPnm) {
      const productIds = get(product, 'bundles[0].games', []).map(game => game._id);
      store.dispatch(addAll(productIds));
      return history.push('/cart');
    }
    await store.dispatch(addToCart(history.push, productId, tierIndex));
    return setAddingToBasket(false);
  };

  return (
    <InView
      rootMargin="0px 0px 400px 0px"
      threshold={0}
    >
      {({ inView, ref }) =>
        <div className="ProductAddToCartBtnContainer" ref={ref}>

          <AddToCart
            addingToBasket={addingToBasket}
            inCart={inCart}
            updateTier={inCart && selectedTier !== inCartTier}
            disableButton={disableButton}
            oldStyle={oldStyle}
            onClick={() => (inCart && selectedTier === inCartTier ? history.push('/cart') : clickAddToCart(selectedTier))}
          />

          {!inView && !giveaway && showStickyHeader &&
            <Suspense fallback={<div />}>
              {isAboveMd &&
                <StickyAddToCart
                  selectedTier={selectedTier}
                  inCartTier={inCartTier}
                  product={product}
                  prerelease={dates.prerelease}
                  clickAddToCart={clickAddToCart}
                  hideDiscount={hideDiscount}
                  addingToBasket={addingToBasket}
                  discountPercentOverride={discountPercentOverride}
                  inCart={inCart}
                  validCoupon={productHasValidCoupon}
                  totalSavingPercent={totalSaving.percent}
                  couponDiscountPercent={(get(validCoupon, 'percent', 0) / 100)}
                  push={history.push}
                />
              }
              {isBelowLg &&
                <DockedAddToCart
                  selectedTier={selectedTier}
                  inCartTier={inCartTier}
                  product={product}
                  clickAddToCart={clickAddToCart}
                  addingToBasket={addingToBasket}
                  hideDiscount={hideDiscount}
                  discountPercentOverride={discountPercentOverride}
                  inCart={inCart}
                  push={history.push}
                  validCoupon={productHasValidCoupon}
                  totalSavingPercent={totalSaving.percent}
                  couponDiscountPercent={(get(validCoupon, 'percent', 0) / 100)}
                />
              }
            </Suspense>
          }
        </div>
      }
    </InView>
  );
};

ProductAddToCartBtn.propTypes = {
  product: PropTypes.shape({}),
  subscribed: PropTypes.bool,
  localNewsletterConsent: PropTypes.number,
  allSlugs: PropTypes.arrayOf(PropTypes.string),
  voucher: PropTypes.shape({}),
  showStickyHeader: PropTypes.bool,
  oldStyle: PropTypes.bool,
  preSelectedPnm: PropTypes.bool,
  selectedTier: PropTypes.number,
  inCartTier: PropTypes.number,
};

ProductAddToCartBtn.defaultProps = {
  product: null,
  subscribed: false,
  localNewsletterConsent: 0,
  allSlugs: [],
  voucher: null,
  showStickyHeader: true,
  oldStyle: false,
  preSelectedPnm: false,
  selectedTier: -1,
  inCartTier: -1,
};


const mapStateToProps = ({
  cart: { allSlugs },
  auth: { email_newsletter: emailNewsletter, localNewsletterConsent },
  voucher: { voucher },
}) => ({
  allSlugs,
  subscribed: emailNewsletter,
  localNewsletterConsent,
  voucher,
});


export default connect(mapStateToProps)(ProductAddToCartBtn);
